import { useMemo, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  parseISO,
} from 'date-fns';
import { useAppData } from '../store';
import ObligationCard from './ObligationCard';

export default function CalendarView() {
  const { obligations, remindersByObligation } = useAppData();
  const [cursor, setCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const active = obligations.filter((o) => o.active);

  const byDate = useMemo(() => {
    const map: Record<string, typeof active> = {};
    for (const o of active) {
      (map[o.due_date] ??= []).push(o);
    }
    return map;
  }, [active]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const selectedKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const selectedObligations = selectedKey ? byDate[selectedKey] ?? [] : [];

  return (
    <div className="calendar-view">
      <div className="page-header">
        <h1>{format(cursor, 'MMMM yyyy')}</h1>
        <div className="page-header-actions">
          <button className="btn-secondary" onClick={() => setCursor((c) => subMonths(c, 1))}>
            ‹
          </button>
          <button className="btn-secondary" onClick={() => setCursor(new Date())}>
            Today
          </button>
          <button className="btn-secondary" onClick={() => setCursor((c) => addMonths(c, 1))}>
            ›
          </button>
        </div>
      </div>

      <div className="calendar-grid">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div className="calendar-dow" key={i}>
            {d}
          </div>
        ))}
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const items = byDate[key] ?? [];
          const inMonth = isSameMonth(day, cursor);
          const isToday = isSameDay(day, new Date());
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          return (
            <button
              key={key}
              className={`calendar-cell ${inMonth ? '' : 'muted-cell'} ${
                isToday ? 'is-today' : ''
              } ${isSelected ? 'is-selected' : ''}`}
              onClick={() => setSelectedDate(day)}
            >
              <span className="cell-date">{format(day, 'd')}</span>
              {items.length > 0 && <span className="cell-dot" />}
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <div className="calendar-day-detail">
          <h2>{format(selectedDate, 'EEEE, MMMM d')}</h2>
          {selectedObligations.length === 0 ? (
            <p className="muted empty">Nothing due this day.</p>
          ) : (
            <div className="card-list">
              {selectedObligations.map((o) => (
                <ObligationCard key={o.id} obligation={o} reminders={remindersByObligation[o.id]} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// keep date-fns parseISO import used for potential future deep-linking
void parseISO;
